'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Menu,
  Plus,
  DollarSign,
  Calendar,
  ExternalLink,
  Users,
  Ticket
} from 'lucide-react';
import { BranchSelector } from '@/components/dashboard/BranchSelector';

export interface HeaderProps {
  onOpenSidebar: () => void;
  businessSlug?: string;
  onNewBookingClick?: () => void;
}

export function Header({
  onOpenSidebar,
  businessSlug = 'juanrojas',
  onNewBookingClick
}: HeaderProps) {
  const pathname = usePathname();

  // Dynamic section title matching Calendico
  const getSectionTitle = () => {
    if (pathname === '/dashboard') return 'Inicio';
    if (pathname.startsWith('/dashboard/bookings')) return 'Reservas';
    if (pathname.startsWith('/dashboard/sales')) return 'Ventas & Caja';
    if (pathname.startsWith('/dashboard/event-types')) return 'Editar servicios y categorías';
    if (pathname.startsWith('/dashboard/availability')) return 'Horarios';
    if (pathname.startsWith('/dashboard/products')) return 'Productos';
    if (pathname.startsWith('/dashboard/clients')) return 'Clientes CRM';
    if (pathname.startsWith('/dashboard/whatsapp')) return 'WhatsApp';
    if (pathname.startsWith('/dashboard/settings')) return 'Configuración';
    if (pathname.startsWith('/dashboard/branches')) return 'Sucursales';
    return 'Panel de Control';
  };

  return (
    <header className="h-16 px-4 md:px-8 bg-white border-b border-gray-200/80 flex items-center justify-between sticky top-0 z-30 shrink-0 font-[family-name:var(--font-plus-jakarta)]">
      {/* Left side: Mobile Toggle + Section Title + Multisucursal Branch Selector */}
      <div className="flex items-center gap-3 md:gap-5 min-w-0">
        <button
          type="button"
          onClick={onOpenSidebar}
          className="md:hidden w-9 h-9 rounded-xl flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer shrink-0"
          title="Abrir menú"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Section Title (Identical to Calendico top bar title) */}
        <h1 className="text-lg md:text-xl font-bold text-[#005C52] tracking-tight truncate">
          {getSectionTitle()}
        </h1>

        {/* Preserved Multisucursal Selector */}
        <div className="hidden lg:block min-w-0">
          <BranchSelector />
        </div>
      </div>

      {/* Right side: Quick Shortcuts + Highlighted Action */}
      <div className="flex items-center gap-2 md:gap-3 shrink-0">
        {/* Enlace al portal público del comercio */}
        <Link
          href={`/${businessSlug}`}
          target="_blank"
          className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-gray-700 hover:text-[#00A896] hover:bg-[#F0FAF9] border border-gray-200 transition-colors"
          title="Ver página de reservas pública"
        >
          <span>Página pública</span>
          <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
        </Link>

        {/* Acceso rápido a Caja ($) */}
        <Link
          href="/dashboard/sales"
          className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:text-[#00A896] hover:bg-[#F0FAF9] hover:border-[#00A896]/40 transition-colors shadow-2xs"
          title="Caja y Cobros"
        >
          <DollarSign className="w-4 h-4" />
        </Link>

        {/* Acceso rápido a Agenda (Calendario) */}
        <Link
          href="/dashboard/bookings"
          className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:text-[#00A896] hover:bg-[#F0FAF9] hover:border-[#00A896]/40 transition-colors shadow-2xs"
          title="Agenda de Reservas"
        >
          <Calendar className="w-4 h-4" />
        </Link>

        {/* Acceso rápido a Clientes */}
        <Link
          href="/dashboard/clients"
          className="hidden md:flex w-9 h-9 rounded-xl bg-white border border-gray-200 items-center justify-center text-gray-600 hover:text-[#00A896] hover:bg-[#F0FAF9] hover:border-[#00A896]/40 transition-colors shadow-2xs"
          title="Directorio de Clientes"
        >
          <Users className="w-4 h-4" />
        </Link>

        {/* Botón Primario: + Nuevo Turno (Turquesa #00A896) */}
        {onNewBookingClick ? (
          <button
            type="button"
            onClick={onNewBookingClick}
            className="h-9 md:h-10 px-3.5 md:px-4 rounded-xl bg-[#00A896] hover:bg-[#009181] active:bg-[#00776B] text-white text-xs md:text-sm font-semibold flex items-center gap-1.5 shadow-sm shadow-[#00A896]/20 hover:shadow transition-all duration-150 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nuevo Turno</span>
            <span className="sm:hidden">Turno</span>
          </button>
        ) : (
          <Link
            href="/dashboard/bookings?action=new"
            className="h-9 md:h-10 px-3.5 md:px-4 rounded-xl bg-[#00A896] hover:bg-[#009181] active:bg-[#00776B] text-white text-xs md:text-sm font-semibold flex items-center gap-1.5 shadow-sm shadow-[#00A896]/20 hover:shadow transition-all duration-150"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nuevo Turno</span>
            <span className="sm:hidden">Turno</span>
          </Link>
        )}
      </div>
    </header>
  );
}
